import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { activeClientState } from '../state.js';
import { ClientAvatar } from '../components/ClientPicker.js';
import { NewClientCard } from '../components/admin/NewClientCard.js';
import { SystemBenchmarkCard } from '../components/admin/SystemBenchmarkCard.js';
import { MonthlyPdfCard } from '../components/admin/MonthlyPdfCard.js';
import { AlternativesCard } from '../components/admin/AlternativesCard.js';
import { PolicyCard } from '../components/admin/PolicyCard.js';
import { ExportCard } from '../components/admin/ExportCard.js';
import { AdvisorMainCard, AdvisorClientsCard } from '../components/admin/advisor/AdvisorSummaryCard.js';
import { AdminSideNav, type AdminGroup } from '../components/admin/AdminSideNav.js';
import { AdminDataSection, useAdminDataCounts, type DataSection } from './AdminDataPage.js';
import { useApi } from '../hooks.js';
import type { ClientInfo } from '@agio/core';

/**
 * One admin area, replacing the old split between "קליטת נתונים" and
 * "בקרת נתונים".
 *
 * Uploading a month and checking what it did are the same job; keeping them on
 * separate top-level pages meant going back and forth between them. The section
 * lives in the URL (/admin/<section>) so any screen stays linkable.
 */
const SECTIONS = ['monthly', 'alternatives', 'policy', 'advisor-clients', 'new-client', 'benchmark', 'advisor', 'clients', 'funds', 'jobs', 'snapshots', 'issues', 'export'] as const;
type SectionId = (typeof SECTIONS)[number];

const TITLE: Record<SectionId, string> = {
  monthly: 'עדכון חודשי',
  alternatives: 'השקעות אלטרנטיביות',
  policy: 'מדיניות השקעות',
  'new-client': 'לקוח חדש',
  benchmark: 'מדד ייחוס',
  'advisor-clients': 'סקירת יועץ',
  advisor: 'סקירת יועץ ראשית',
  clients: 'לקוחות',
  funds: 'תיקים',
  jobs: 'קליטות אחרונות',
  snapshots: 'תמונות מצב',
  issues: 'איכות נתונים',
  export: 'ייצוא נתונים',
};

/** Screens that are about the whole system or every client, not the one picked
 *  in the header. Every other screen works on the header's client. */
const NOT_PER_CLIENT: readonly SectionId[] = ['new-client', 'benchmark', 'advisor', 'clients'];

const isSection = (v: string | undefined): v is SectionId =>
  !!v && (SECTIONS as readonly string[]).includes(v);

export function AdminPage() {
  const { section } = useParams();
  const navigate = useNavigate();
  const active: SectionId = isSection(section) ? section : 'monthly';
  // The per-client step used to be a tab of the main advisor screen; old links land on its new home.
  const [search] = useSearchParams();
  useEffect(() => {
    if (active === 'advisor' && search.get('tab') === 'clients') navigate('/admin/advisor-clients', { replace: true });
  }, [active, search, navigate]);
  const counts = useAdminDataCounts();
  const { data: clients } = useApi<ClientInfo[]>('/clients');
  const clientId = useRecoilValue(activeClientState);
  const client = NOT_PER_CLIENT.includes(active) ? undefined : clients?.find((c) => c.client_id === clientId);

  const groups: AdminGroup[] = [
    {
      // Everything that updates the client picked in the header.
      title: 'עדכון לקוח',
      sections: [
        { id: 'monthly', label: TITLE.monthly },
        { id: 'alternatives', label: TITLE.alternatives },
        { id: 'policy', label: TITLE.policy },
        { id: 'advisor-clients', label: TITLE['advisor-clients'] },
      ],
    },
    {
      title: 'תוכן',
      sections: [{ id: 'advisor', label: TITLE.advisor }],
    },
    {
      title: 'בקרה',
      sections: [
        { id: 'clients', label: TITLE.clients },
        { id: 'funds', label: TITLE.funds },
        { id: 'jobs', label: TITLE.jobs, badge: counts.jobs },
        { id: 'snapshots', label: TITLE.snapshots },
        { id: 'issues', label: TITLE.issues, badge: counts.errors, alert: true },
        { id: 'export', label: TITLE.export },
      ],
    },
    {
      // Not about an existing client: opening a new one, and the index data every client shares.
      title: 'מערכת',
      sections: [
        { id: 'new-client', label: TITLE['new-client'] },
        { id: 'benchmark', label: TITLE.benchmark },
      ],
    },
  ];

  return (
    <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-8">
      <AdminSideNav groups={groups} active={active} onChange={(id) => navigate(`/admin/${id}`)} />

      {/* min-w-0 is what stops a wide table from pushing the whole page sideways. */}
      <div className="flex min-w-0 flex-1 flex-col gap-6">
        <h1 className="flex flex-wrap items-center gap-x-3 gap-y-1 text-2xl font-bold">
          {TITLE[active]}
          {/* The header's picker chooses the client for every screen here; saying
              which one, right on the title, is what replaces the per-screen
              dropdowns this area used to have. */}
          {client && (
            <span className="flex items-center gap-2 text-lg font-normal text-muted-foreground">
              <span aria-hidden>·</span>
              <ClientAvatar name={client.display_name_he} logoUrl={client.logo_url} size={24} />
              {client.display_name_he}
            </span>
          )}
        </h1>
        {active === 'monthly' && <MonthlyPdfCard />}
        {active === 'alternatives' && <AlternativesCard />}
        {active === 'policy' && <PolicyCard />}
        {active === 'new-client' && <NewClientCard />}
        {active === 'benchmark' && <SystemBenchmarkCard />}
        {active === 'advisor' && <AdvisorMainCard />}
        {active === 'advisor-clients' && <AdvisorClientsCard />}
        {active === 'export' && <ExportCard />}
        {(['clients', 'funds', 'jobs', 'snapshots', 'issues'] as const).includes(active as DataSection) && (
          <AdminDataSection section={active as DataSection} />
        )}
      </div>
    </div>
  );
}
